/**
 * ViewHeader - Header component for view details page
 *
 * Displays view name, badges, description, and action buttons
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, RefreshCw, Database, HardDrive, Trash2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ViewStatusBadge } from '@/components/views/ViewStatusBadge';
import { ViewTypeBadge } from '@/components/views/ViewTypeBadge';
import { useToast } from '@/hooks/useToast';
import {
  useRefreshView,
  useMaterializeView,
  useDematerializeView,
  useDeleteView,
  useAffectedViews,
} from '@/hooks/useViews';
import { ImpactWarningDialog } from '@/components/views/ImpactWarningDialog';
import type { View } from '@/types/views';

interface ViewHeaderProps {
  view: View;
  onMaterializeStarted?: () => void;
}

/**
 * ViewHeader Component
 *
 * Displays view metadata and provides action buttons
 */
export const ViewHeader: React.FC<ViewHeaderProps> = ({ view, onMaterializeStarted }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);

  // Mutations
  const refreshView = useRefreshView();
  const materializeView = useMaterializeView();
  const dematerializeView = useDematerializeView();
  const deleteView = useDeleteView();
  const { data: affectedViews } = useAffectedViews(view.id);

  /**
   * Handle refresh view
   */
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const response = await refreshView.mutateAsync({
        view_id: view.id,
        force: true,
      });
      toast({
        title: 'Refresh Started',
        description: `View refresh initiated. Refresh ID: ${response.refresh_id}`,
      });
    } catch (error: any) {
      toast({
        title: 'Refresh Failed',
        description: error.response?.data?.detail || 'Failed to trigger refresh',
        variant: 'destructive',
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  /**
   * Handle materialize/dematerialize
   */
  const handleToggleMaterialization = async () => {
    if (view.view_type === 'virtual') {
      // Materialize
      const confirmed = window.confirm(
        `Materialize view "${view.name}"?\n\nThis will pre-compute and store results as Parquet files, which may take several minutes.`
      );
      if (!confirmed) return;

      try {
        await materializeView.mutateAsync({ view_id: view.id });
        onMaterializeStarted?.();
        toast({
          title: 'Materialization Started',
          description: 'View is being materialized. The badge will update automatically when complete.',
        });
      } catch (error: any) {
        toast({
          title: 'Materialization Failed',
          description: error.response?.data?.detail || 'Failed to materialize view',
          variant: 'destructive',
        });
      }
    } else {
      // Dematerialize
      const confirmed = window.confirm(
        `Convert "${view.name}" to virtual view?\n\nThis will delete the stored Parquet files. The view will query files directly.`
      );
      if (!confirmed) return;

      try {
        await dematerializeView.mutateAsync(view.id);
        toast({
          title: 'View Dematerialized',
          description: 'View has been converted to virtual',
        });
      } catch (error: any) {
        toast({
          title: 'Dematerialization Failed',
          description: error.response?.data?.detail || 'Failed to dematerialize view',
          variant: 'destructive',
        });
      }
    }
  };

  /**
   * Handle delete view
   */
  const handleDelete = async () => {
    // If there are affected views, show impact warning dialog
    if (affectedViews && affectedViews.length > 0) {
      setShowDeleteWarning(true);
      return;
    }

    // No affected views, show simple confirmation
    const confirmed = window.confirm(
      `Delete view "${view.name}"?\n\n` +
        'This will remove the view and all associated refresh history. ' +
        (view.view_type === 'materialized' ? 'Stored data will also be deleted. ' : '') +
        'This action cannot be undone.'
    );

    if (!confirmed) return;

    await performDelete();
  };

  /**
   * Perform the actual delete operation
   */
  const performDelete = async () => {
    try {
      await deleteView.mutateAsync(view.id);
      toast({
        title: 'View Deleted',
        description: `View "${view.name}" has been deleted successfully`,
      });
      setShowDeleteWarning(false);
      navigate('/views');
    } catch (error: any) {
      toast({
        title: 'Delete Failed',
        description: error.response?.data?.detail || 'Failed to delete view',
        variant: 'destructive',
      });
      setShowDeleteWarning(false);
    }
  };

  return (
    <>
      {/* Impact Warning Dialog */}
      {affectedViews && affectedViews.length > 0 && (
        <ImpactWarningDialog
          isOpen={showDeleteWarning}
          affectedViews={affectedViews}
          changeType="delete"
          viewName={view.name}
          onConfirm={performDelete}
          onCancel={() => setShowDeleteWarning(false)}
        />
      )}

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
          {/* Title and Badges */}
          <div className="flex items-start justify-between">
            <div className="space-y-3 flex-1">
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold">{view.name}</h1>
                <ViewStatusBadge status={view.status} />
                <ViewTypeBadge type={view.is_materialized ? 'materialized' : view.view_type} showIcon />
              </div>
              {view.description && (
                <p className="text-muted-foreground">{view.description}</p>
              )}
              {view.tags && view.tags.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {view.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-info/10 text-info"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => navigate(`/views/${view.id}/edit`)}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
              <Button
                variant="outline"
                onClick={handleRefresh}
                disabled={isRefreshing || view.status === 'building'}
              >
                {isRefreshing || view.status === 'building' ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Refresh
              </Button>
              <Button
                variant="outline"
                onClick={handleToggleMaterialization}
                disabled={
                  materializeView.isPending ||
                  dematerializeView.isPending ||
                  view.status === 'building'
                }
              >
                {materializeView.isPending || dematerializeView.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : view.view_type === 'virtual' ? (
                  <HardDrive className="h-4 w-4 mr-2" />
                ) : (
                  <Database className="h-4 w-4 mr-2" />
                )}
                {view.view_type === 'virtual' ? 'Materialize' : 'Convert to Virtual'}
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={deleteView.isPending}
              >
                {deleteView.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Delete
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
    </>
  );
};
